import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@choose/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@choose/ui/alert";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@choose/ui/input-group";
import { createFileRoute } from "@tanstack/react-router";
import { SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { AppContactList } from "~/components/common/contacts/app-contact-list";
import { EmailContactList } from "~/components/common/contacts/email-contact-list";
import {
  DEFAULT_COLUMN_VISIBILITY as ORDER_DEFAULT_COLUMN_VISIBILITY,
  SELECTABLE_TABLE_DEFAULT_STATE as ORDER_SELECTABLE_TABLE_DEFAULT_STATE,
  OrderList,
} from "~/components/common/orders/order-list";
import {
  SELECTABLE_TABLE_DEFAULT_STATE as SALE_SELECTABLE_TABLE_DEFAULT_STATE,
  SaleList,
} from "~/components/common/sales/sale-list";
import {
  Orders_Search_Filter_Search_Field_Input,
  Orders_Search_Sort_Field_Input,
  Orders_Search_Sort_Order_Input,
  useSearchAppContactsQuery,
  useSearchEmailContactsQuery,
  useSearchOrdersQuery,
  useSearchSalesQuery,
} from "~/graphql/graphql.gen";
import useDebounce from "~/hooks/use-debounce";

const searchQueryParamsSchema = z.object({
  q: z.string().optional(),
});

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/search/",
)({
  component: RouteComponent,
  validateSearch: (search) => searchQueryParamsSchema.parse(search),
});

function RouteComponent() {
  const { t } = useTranslation();
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchTerm, setSearchTerm] = useState(q ?? "");
  const debouncedSearchTerm = useDebounce(q, 100);

  const { data: appContacts } = useSearchAppContactsQuery(
    {
      input: {
        term: debouncedSearchTerm ?? "",
        limit: 5,
      },
    },
    {
      enabled: !!q && q.length > 0,
      select: (data) => data.searchSupportActorAppContact,
    },
  );

  const { data: emailContacts } = useSearchEmailContactsQuery(
    {
      input: {
        term: debouncedSearchTerm ?? "",
        limit: 5,
      },
    },
    {
      enabled: !!q && q.length > 0,
      select: (data) => data.searchSupportActorEmailContacts,
    },
  );

  const { data: orders } = useSearchOrdersQuery(
    {
      input: {
        search: {
          field: Orders_Search_Filter_Search_Field_Input.All,
          term: debouncedSearchTerm ?? "",
        },
        sort: {
          field: Orders_Search_Sort_Field_Input.CreationDate,
          order: Orders_Search_Sort_Order_Input.Descending,
        },
        pageInfo: { first: 5 },
      },
    },
    {
      enabled: !!q && q.length > 0,
      select: (data) => data.orders_search,
    },
  );

  const { data: sales } = useSearchSalesQuery(
    {
      input: {
        term: debouncedSearchTerm ?? "",
        limit: 5,
      },
    },
    {
      enabled: !!q && q.length > 0,
      select: (data) => data.searchSales,
    },
  );

  return (
    <div className="bg-background flex w-full flex-col md:rounded-md md:border">
      <div className="flex h-full grow flex-col">
        <header className="flex flex-col items-center">
          <div className="flex h-10 w-full items-center gap-2 border-b px-4">
            <InputGroup className="border-none ring-0!">
              <InputGroupAddon align="inline-start">
                <SearchIcon />
              </InputGroupAddon>
              <InputGroupInput
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  void navigate({
                    search: { q: e.target.value },
                    replace: true,
                  });
                }}
                placeholder=""
                id="search-input"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              {q ? (
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    size="icon-xs"
                    onClick={() => navigate({ search: { q: "" } })}
                  >
                    <XIcon />
                  </InputGroupButton>
                </InputGroupAddon>
              ) : null}
            </InputGroup>
          </div>
        </header>
        <div className="h-full w-full overflow-auto">
          {!q ? (
            <Alert className="bg-sidebar mx-auto mt-28 max-w-md">
              <SearchIcon />
              <AlertTitle>{t("search.empty_state.title")}</AlertTitle>
              <AlertDescription>
                {t("search.empty_state.description")}
              </AlertDescription>
            </Alert>
          ) : null}
          <Accordion
            multiple
            defaultValue={["appContact", "emailContact", "order", "sale"]}
          >
            {appContacts && appContacts.length > 0 ? (
              <AccordionItem value="appContact">
                <AccordionTrigger className="bg-sidebar-accent rounded-none px-4">
                  {t("search.tabs.app_contacts")}
                </AccordionTrigger>
                <AccordionContent className="pb-0 [&_a]:no-underline">
                  <AppContactList
                    data={appContacts}
                    state={{
                      columnOrder: ["select", "channel", "name", "email"],
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            ) : null}
            {emailContacts && emailContacts.length > 0 ? (
              <AccordionItem value="emailContact">
                <AccordionTrigger className="bg-sidebar-accent rounded-none px-4">
                  {t("search.tabs.email_contacts")}
                </AccordionTrigger>
                <AccordionContent className="pb-0 [&_a]:no-underline">
                  <EmailContactList
                    data={emailContacts}
                    state={{
                      columnOrder: ["select", "channel", "name", "email"],
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            ) : null}
            {orders && orders.edges.length > 0 ? (
              <AccordionItem value="order">
                <AccordionTrigger className="bg-sidebar-accent rounded-none px-4">
                  {t("search.tabs.orders")}
                </AccordionTrigger>
                <AccordionContent className="pb-0 [&_a]:no-underline">
                  <OrderList
                    data={orders.edges.map((edge) => edge.node)}
                    state={{
                      ...ORDER_SELECTABLE_TABLE_DEFAULT_STATE,
                      columnVisibility: ORDER_DEFAULT_COLUMN_VISIBILITY,
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            ) : null}
            {sales && sales.length > 0 ? (
              <AccordionItem value="sale">
                <AccordionTrigger className="bg-sidebar-accent rounded-none px-4">
                  {t("search.tabs.sales")}
                </AccordionTrigger>
                <AccordionContent className="pb-0 [&_a]:no-underline">
                  <SaleList
                    data={sales}
                    state={{
                      ...SALE_SELECTABLE_TABLE_DEFAULT_STATE,
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            ) : null}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
